import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CreditCard, BarChart3, Key, Plus, Copy, Trash2, Book, LogOut, Settings, TrendingUp, Home, User, Shield } from 'lucide-react';
import axios from 'axios';
import Subscription from './Subscription';
import './SharedStyles.css';

interface UsageData {
  date: string;
  requests: number;
}

interface ApiKey {
  id: string;
  name: string;
  key: string;
  created: string;
  lastUsed: string | null;
  isActive: boolean;
}

interface UserUsage {
  totalRequests: number;
  usage: Record<string, number>;
  lastSync: number;
  userId: string;
  plan: string;
  limit: number;
  remaining: number;
}

type TabType = 'overview' | 'api-keys' | 'analytics' | 'subscription' | 'settings';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [usageData, setUsageData] = useState<UsageData[]>([]);
  const [userUsage, setUserUsage] = useState<UserUsage | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewKeyForm, setShowNewKeyForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteKeyId, setPendingDeleteKeyId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      loadDashboardData();
    }
  }, [user, loading, navigate]);

  const loadDashboardData = async () => {
    try {
      const [usageResponse, keysResponse] = await Promise.all([
        axios.get('/user/usage'),
        axios.get('/user/api-keys')
      ]);

      const usageData = usageResponse.data.data;
      if (usageData) {
        setUserUsage(usageData);
        if (usageData.historicalData && Array.isArray(usageData.historicalData)) {
          setUsageData(usageData.historicalData);
        } else {
          setUsageData([]);
        }
      }
      const apiKeyData = keysResponse.data && keysResponse.data.data;
      setApiKeys(Array.isArray(apiKeyData?.keys) ? apiKeyData.keys : []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setUserUsage({
        totalRequests: 0,
        usage: {},
        lastSync: Date.now(),
        userId: '',
        plan: 'starter',
        limit: 1000,
        remaining: 1000
      });
      setUsageData([]);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) return;

    setCreatingKey(true);
    try {
      const response = await axios.post('/user/api-keys', {
        name: newKeyName
      });

      setApiKeys([...apiKeys, response.data.data]);
      setNewKeyName('');
      setShowNewKeyForm(false);
    } catch (error: any) {
      setErrorMessage('Fehler beim Erstellen des API-Schlüssels: ' + (error.response?.data?.error || error.message));
    } finally {
      setCreatingKey(false);
    }
  };

  const deleteApiKey = (keyId: string) => {
    setShowDeleteModal(true);
    setPendingDeleteKeyId(keyId);
  };

  const confirmDeleteApiKey = async () => {
    if (!pendingDeleteKeyId) return;
    try {
      await axios.delete(`/user/api-keys/${pendingDeleteKeyId}`);
      setApiKeys(apiKeys.filter(key => key.id !== pendingDeleteKeyId));
      setShowDeleteModal(false);
      setPendingDeleteKeyId(null);
    } catch (error: any) {
      setErrorMessage('Fehler beim Löschen des API-Schlüssels: ' + (error.response?.data?.error || error.message));
      setShowDeleteModal(false);
      setPendingDeleteKeyId(null);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await axios.delete('/user/account');
      setShowDeleteAccountModal(false);
      setShowSuccessMessage('Ihr Konto wurde erfolgreich gelöscht. Sie werden nun abgemeldet.');
      
      setTimeout(() => {
        logout();
        navigate('/');
      }, 2000);
    } catch (error: any) {
      setErrorMessage('Fehler beim Löschen des Kontos: ' + (error.response?.data?.error || error.message));
      setShowDeleteAccountModal(false);
    } finally {
      setDeletingAccount(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getPlanBadgeStyle = (plan: string) => {
    switch (plan) {
      case 'starter': return { background: 'var(--success-green)', color: 'white' };
      case 'professional': return { background: 'var(--accent-blue)', color: 'white' };
      case 'business': return { background: 'var(--primary-medium)', color: 'white' };
      case 'enterprise': return { background: 'var(--text-dark)', color: 'white' };
      default: return { background: 'var(--text-light)', color: 'white' };
    }
  };

  const getUsagePercentage = () => {
    if (!userUsage?.limit || userUsage.limit === 0) return 0;
    return (userUsage.totalRequests / userUsage.limit) * 100;
  };

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: Home },
    { id: 'api-keys', label: 'API-Schlüssel', icon: Key },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'subscription', label: 'Abonnement', icon: CreditCard },
    { id: 'settings', label: 'Einstellungen', icon: Settings }
  ];

  if (loading) {
    return (
      <div className="app-page">
        <div className="app-main flex items-center justify-center">
          <div className="text-center">
            <div className="app-loading" style={{ width: '3rem', height: '3rem', margin: '0 auto' }}></div>
            <p style={{ marginTop: '1rem', color: 'var(--text-medium)' }}>Dashboard wird geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="app-page">
      {/* Header */}
      <header className="app-nav">
        <nav className="app-nav-content">
          <div className="flex items-center gap-4">
            <Link to="/" className="app-logo">
              Pfändungstabelle API
            </Link>
            <span style={{ color: 'var(--text-light)' }}>Dashboard</span>
          </div>
          <div className="app-nav-links">
            {user.role === 'admin' && (
              <Link
                to="/admin"
                className="app-button app-button-secondary flex items-center gap-2"
                style={{ textDecoration: 'none' }}
              >
                <Shield size={16} />
                Admin
              </Link>
            )}
            <span style={{ color: 'var(--text-medium)' }}>Willkommen, {user.email}</span>
            <button
              onClick={logout}
              className="app-button app-button-secondary flex items-center gap-2"
            >
              <LogOut size={16} />
              Abmelden
            </button>
          </div>
        </nav>
      </header>

      <main className="app-main">
        <div className="app-container">
          {/* Tab Navigation and Content Container */}
          <div style={{ 
            background: 'var(--background-white)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {/* Tab Navigation */}
            <div style={{ 
              borderBottom: '1px solid var(--border-light)', 
              padding: '0 1rem'
            }}>
              <div style={{ display: 'flex', gap: '0' }}>
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as TabType)}
                      style={{
                        padding: '1rem 1.5rem',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        color: activeTab === tab.id ? 'var(--voidmind-primary)' : 'var(--text-medium)',
                        borderBottom: activeTab === tab.id ? '2px solid var(--voidmind-primary)' : '2px solid transparent',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Icon size={18} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div style={{ padding: '2rem' }}>
            {activeTab === 'overview' && (
              <div>
                <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: 'var(--text-dark)' }}>
                  Dashboard Übersicht
                </h2>
                
                {/* Overview Cards */}
                <div className="app-card-grid" style={{ marginBottom: '2rem' }}>
                  {/* Plan Card */}
                  <div className="app-card">
                    <div className="flex items-center justify-between mb-4">
                      <div style={{ 
                        width: '60px', 
                        height: '60px', 
                        background: 'var(--primary-medium)', 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        <CreditCard size={24} />
                      </div>
                      <span 
                        style={{
                          ...getPlanBadgeStyle(user.plan),
                          padding: '0.5rem 1rem',
                          borderRadius: '20px',
                          fontSize: '0.875rem',
                          fontWeight: '600'
                        }}
                      >
                        Aktiv
                      </span>
                    </div>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', marginBottom: '0.5rem' }}>
                      Aktueller Plan
                    </h3>
                    <p style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--text-dark)', marginBottom: '1rem', textTransform: 'capitalize' }}>
                      {user.plan}
                    </p>
                    <button
                      onClick={() => setActiveTab('subscription')}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: 'var(--voidmind-primary)', 
                        fontSize: '0.875rem', 
                        fontWeight: '600',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      Plan ändern →
                    </button>
                  </div>

                  {/* Usage Card */}
                  <div className="app-card">
                    <div className="flex items-center justify-between mb-4">
                      <div style={{ 
                        width: '60px', 
                        height: '60px', 
                        background: 'var(--primary-medium)', 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        <BarChart3 size={24} />
                      </div>
                    </div>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', marginBottom: '0.5rem' }}>
                      API-Nutzung
                    </h3>
                    <p style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--text-dark)', marginBottom: '0.5rem' }}>
                      {userUsage?.totalRequests?.toLocaleString() || '0'}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '1rem' }}>
                      von {userUsage?.limit === Infinity ? 'unbegrenzt' : userUsage?.limit?.toLocaleString() || '1000'}
                    </p>
                    <div style={{ width: '100%', background: 'var(--border-light)', borderRadius: '4px', height: '8px', marginBottom: '0.5rem' }}>
                      <div
                        style={{ 
                          background: 'var(--voidmind-primary)', 
                          height: '8px', 
                          borderRadius: '4px',
                          width: `${Math.min(getUsagePercentage(), 100)}%`
                        }}
                      ></div>
                    </div>
                    <button
                      onClick={() => setActiveTab('analytics')}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: 'var(--voidmind-primary)', 
                        fontSize: '0.875rem', 
                        fontWeight: '600',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      Details anzeigen →
                    </button>
                  </div>

                  {/* API Keys Card */}
                  <div className="app-card">
                    <div className="flex items-center justify-between mb-4">
                      <div style={{ 
                        width: '60px', 
                        height: '60px', 
                        background: 'var(--primary-medium)', 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        <Key size={24} />
                      </div>
                    </div>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', marginBottom: '0.5rem' }}>
                      API-Schlüssel
                    </h3>
                    <p style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--text-dark)', marginBottom: '1rem' }}>
                      {apiKeys.length}
                    </p>
                    <button
                      onClick={() => setActiveTab('api-keys')}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: 'var(--voidmind-primary)', 
                        fontSize: '0.875rem', 
                        fontWeight: '600',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      Verwalten →
                    </button>
                  </div>
                </div>

                {/* Quick Actions */}
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-dark)' }}>
                    Schnellzugriff
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                    <button
                      onClick={() => setActiveTab('api-keys')}
                      style={{
                        padding: '1.5rem',
                        border: '1px solid var(--border-light)',
                        borderRadius: '8px',
                        background: 'var(--background-light)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-white)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--background-light)'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <Plus size={20} style={{ color: 'var(--voidmind-primary)' }} />
                        <div>
                          <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'var(--text-dark)' }}>
                            Neuen API-Schlüssel erstellen
                          </h4>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-medium)' }}>
                            Erstellen Sie einen neuen Schlüssel für Ihre Anwendung
                          </p>
                        </div>
                      </div>
                    </button>
                    
                    <Link
                      to="https://api.pfaendungstabelle.voidmind.io/docs"
                      style={{
                        padding: '1.5rem',
                        border: '1px solid var(--border-light)',
                        borderRadius: '8px',
                        background: 'var(--background-light)',
                        textDecoration: 'none',
                        display: 'block',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-white)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--background-light)'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <Book size={20} style={{ color: 'var(--voidmind-primary)' }} />
                        <div>
                          <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'var(--text-dark)' }}>
                            API-Dokumentation
                          </h4>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-medium)' }}>
                            Vollständige Dokumentation und Beispiele
                          </p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'api-keys' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--text-dark)', margin: 0 }}>
                    API-Schlüssel
                  </h2>
                  <button
                    onClick={() => setShowNewKeyForm(true)}
                    className="app-button app-button-primary flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Neuen Schlüssel erstellen
                  </button>
                </div>

                {/* New Key Form */}
                {showNewKeyForm && (
                  <div className="app-alert app-alert-info" style={{ marginBottom: '2rem' }}>
                    <div className="flex items-center gap-4">
                      <input
                        type="text"
                        placeholder="Schlüssel-Name (z.B. 'Produktions-API')"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        className="app-input"
                        style={{ flex: 1 }}
                      />
                      <button
                        onClick={createApiKey}
                        disabled={creatingKey || !newKeyName.trim()}
                        className="app-button app-button-primary"
                      >
                        {creatingKey ? (
                          <>
                            <span className="app-loading" style={{ marginRight: '0.5rem' }}></span>
                            Erstelle...
                          </>
                        ) : (
                          'Erstellen'
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowNewKeyForm(false);
                          setNewKeyName('');
                        }}
                        className="app-button app-button-secondary"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}

                {/* API Keys List */}
                {apiKeys.length === 0 ? (
                  <div className="text-center" style={{ padding: '3rem 0' }}>
                    <p style={{ color: 'var(--text-light)', marginBottom: '1rem' }}>
                      Noch keine API-Schlüssel erstellt.
                    </p>
                    <button
                      onClick={() => setShowNewKeyForm(true)}
                      className="app-button app-button-primary"
                    >
                      Ersten Schlüssel erstellen
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {apiKeys.map((key) => (
                      <div 
                        key={key.id} 
                        style={{ 
                          padding: '1.5rem', 
                          border: '1px solid var(--border-light)', 
                          borderRadius: '8px',
                          background: 'var(--background-light)'
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div style={{ flex: 1 }}>
                            <div className="flex items-center gap-3 mb-2">
                              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-dark)', margin: 0 }}>
                                {key.name}
                              </h4>
                              <span 
                                style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  background: key.isActive ? 'var(--success-green)' : 'var(--error-red)',
                                  color: 'white'
                                }}
                              >
                                {key.isActive ? 'Aktiv' : 'Inaktiv'}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mb-3" style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                              <span>
                                Erstellt: {key.created ? new Date(key.created).toLocaleDateString('de-DE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                }) : '-'}
                              </span>
                              {key.lastUsed && (
                                <span>
                                  Zuletzt verwendet: {key.lastUsed ? new Date(key.lastUsed).toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  }) : '-'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <code style={{ 
                                background: 'var(--background-white)', 
                                padding: '0.5rem', 
                                borderRadius: '4px', 
                                fontSize: '0.875rem',
                                fontFamily: 'monospace',
                                border: '1px solid var(--border-light)'
                              }}>
                                {key.key.substring(0, 20)}...
                              </code>
                              <button
                                onClick={() => copyToClipboard(key.key)}
                                className="app-button app-button-secondary flex items-center gap-1"
                                style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                              >
                                <Copy size={14} />
                                Kopieren
                              </button>
                            </div>
                          </div>
                          <button
                            onClick={() => deleteApiKey(key.id)}
                            className="app-button flex items-center gap-1"
                            style={{ 
                              background: 'var(--error-red)', 
                              color: 'white',
                              fontSize: '0.875rem',
                              padding: '0.5rem 1rem'
                            }}
                          >
                            <Trash2 size={14} />
                            Löschen
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'analytics' && (
              <div>
                <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: 'var(--text-dark)' }}>
                  Nutzungsanalyse
                </h2>
                
                {/* Usage Chart */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-dark)' }}>
                    API-Nutzung (letzte 30 Tage)
                  </h3>
                  <div style={{ 
                    height: '20rem',
                    width: '100%',
                    background: 'var(--background-light)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-light)',
                    padding: '1rem',
                    boxSizing: 'border-box'
                  }}>
                    {usageData.length === 0 ? (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%'
                      }}>
                        <div style={{ textAlign: 'center', color: 'var(--text-light)' }}>
                          <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Noch keine API-Nutzung</p>
                          <p style={{ fontSize: '0.875rem' }}>Erstellen Sie einen API-Schlüssel und starten Sie Ihre ersten Anfragen</p>
                        </div>
                      </div>
                    ) : (
                      <div style={{ width: '100%', height: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart 
                            data={usageData}
                            margin={{ top: 10, right: 20, left: 5, bottom: 40 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tick={{ fontSize: 12 }}
                              angle={-45}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis 
                              tick={{ fontSize: 12 }}
                              width={30}
                            />
                            <Tooltip />
                            <Line type="monotone" dataKey="requests" stroke="var(--voidmind-primary)" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>

                {/* Usage Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', marginBottom: '0.5rem' }}>
                      Gesamt-Anfragen
                    </h4>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-dark)', margin: 0 }}>
                      {userUsage?.totalRequests.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', marginBottom: '0.5rem' }}>
                      Verbleibendes Limit
                    </h4>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-dark)', margin: 0 }}>
                      {userUsage?.remaining?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', marginBottom: '0.5rem' }}>
                      Nutzung in %
                    </h4>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-dark)', margin: 0 }}>
                      {getUsagePercentage().toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div>
                <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: 'var(--text-dark)' }}>
                  Abonnement verwalten
                </h2>
                <Subscription currentPlan={user.plan} />
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: 'var(--text-dark)' }}>
                  Konto-Einstellungen
                </h2>
                
                {/* Account Info */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-dark)' }}>
                    Kontoinformationen
                  </h3>
                  <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', display: 'block', marginBottom: '0.5rem' }}>
                          E-Mail-Adresse
                        </label>
                        <p style={{ margin: 0, fontSize: '1rem', color: 'var(--text-dark)' }}>
                          {user.email}
                        </p>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', display: 'block', marginBottom: '0.5rem' }}>
                          Aktueller Plan
                        </label>
                        <p style={{ margin: 0, fontSize: '1rem', color: 'var(--text-dark)', textTransform: 'capitalize' }}>
                          {user.plan}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* API Documentation */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-dark)' }}>
                    API-Dokumentation
                  </h3>
                  <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '0.5rem' }}>
                          Basis-URL
                        </h4>
                        <code style={{ 
                          display: 'block', 
                          background: 'var(--background-white)', 
                          padding: '1rem', 
                          borderRadius: '6px', 
                          fontSize: '0.875rem',
                          fontFamily: 'monospace',
                          border: '1px solid var(--border-light)'
                        }}>
                          https://api.pfaendungstabelle.voidmind.io
                        </code>
                      </div>
                      <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '0.5rem' }}>
                          Authentifizierung
                        </h4>
                        <code style={{ 
                          display: 'block', 
                          background: 'var(--background-white)', 
                          padding: '1rem', 
                          borderRadius: '6px', 
                          fontSize: '0.875rem',
                          fontFamily: 'monospace',
                          border: '1px solid var(--border-light)'
                        }}>
                          Authorization: Bearer YOUR_API_KEY
                        </code>
                      </div>
                    </div>
                    
                    <div style={{ marginTop: '1.5rem' }}>
                      <Link
                        to="https://api.pfaendungstabelle.voidmind.io/docs"
                        className="app-button app-button-primary"
                        style={{ textDecoration: 'none' }}
                      >
                        Vollständige Dokumentation anzeigen →
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Danger Zone */}
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-dark)' }}>
                    Gefahrenbereich
                  </h3>
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '1.5rem', borderRadius: '8px' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#dc2626', marginBottom: '0.5rem' }}>
                      Konto löschen
                    </h4>
                    <p style={{ fontSize: '0.875rem', color: '#7f1d1d', marginBottom: '1rem' }}>
                      Das Löschen Ihres Kontos entfernt alle Ihre Daten permanent, einschließlich API-Schlüssel, Nutzungsstatistiken und Abonnement-Informationen. Diese Aktion kann nicht rückgängig gemacht werden.
                    </p>
                    <button
                      onClick={() => setShowDeleteAccountModal(true)}
                      className="app-button"
                      style={{ 
                        background: '#dc2626', 
                        color: 'white',
                        fontSize: '0.875rem'
                      }}
                    >
                      Konto permanent löschen
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-dark)' }}>
              API-Schlüssel löschen
            </h3>
            <p style={{ margin: '0 0 2rem 0', color: 'var(--text-medium)' }}>
              Sind Sie sicher, dass Sie diesen API-Schlüssel löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="app-button app-button-secondary"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmDeleteApiKey}
                className="app-button"
                style={{ backgroundColor: 'var(--error-red)', color: 'white' }}
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Deletion Confirmation Modal */}
      {showDeleteAccountModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#dc2626', fontSize: '1.25rem', fontWeight: '600' }}>
              Konto permanent löschen
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-medium)', lineHeight: '1.5' }}>
              <strong>Achtung:</strong> Das Löschen Ihres Kontos entfernt alle Ihre Daten permanent, einschließlich:
            </p>
            <ul style={{ margin: '0 0 1.5rem 1.5rem', color: 'var(--text-medium)', lineHeight: '1.5' }}>
              <li>Alle API-Schlüssel</li>
              <li>Nutzungsstatistiken und Verlauf</li>
              <li>Abonnement-Informationen</li>
              <li>Alle gespeicherten Einstellungen</li>
            </ul>
            <p style={{ margin: '0 0 2rem 0', color: '#dc2626', fontWeight: '600' }}>
              Diese Aktion kann nicht rückgängig gemacht werden!
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteAccountModal(false)}
                className="app-button app-button-secondary"
                disabled={deletingAccount}
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                className="app-button"
                style={{ backgroundColor: '#dc2626', color: 'white' }}
              >
                {deletingAccount ? (
                  <>
                    <span className="app-loading" style={{ marginRight: '0.5rem' }}></span>
                    Lösche Konto...
                  </>
                ) : (
                  'Konto permanent löschen'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSuccessMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: 'var(--success-green)',
          color: 'white',
          padding: '1rem',
          borderRadius: '8px',
          maxWidth: '400px',
          zIndex: 1000
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{showSuccessMessage}</span>
            <button
              onClick={() => setShowSuccessMessage(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                marginLeft: '1rem',
                fontSize: '1.2rem'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: 'var(--error-red)',
          color: 'white',
          padding: '1rem',
          borderRadius: '8px',
          maxWidth: '400px',
          zIndex: 1000
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{errorMessage}</span>
            <button
              onClick={() => setErrorMessage(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                marginLeft: '1rem',
                fontSize: '1.2rem'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
